import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { toastr } from "react-redux-toastr";
import api from "APP_ROOT/api";
import { API_BASE } from "APP_ROOT/constants";
import { renderIf } from "MODULES/shared/decorators";
import {
  SearchFilter,
  FilterSelect,
  Pagination,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import { Search, Button } from "MODULES/shared/components";
import { matchesBoolean } from "LIB/filters";
import { SUCCESS } from "LIB/submission";
import DateRangePicker from "MODULES/shared/components/DateRangePicker/DateRangePicker";
import useCurrentUser from "APP_ROOT/hooks/useCurrentUser";
import { ADMIN, EXPORT_LIMIT } from "MODULES/users/constants";
import { hasRole } from "MODULES/users/helpers";
import { stringify } from "query-string";
import Table from "./Table";
import pagination from "../pagination";

const defaultFilters = {
  current_user: undefined,
  no_quote: undefined,
  offer: undefined,
  looking_for: undefined,
  no_interest: undefined,
  standard: undefined,
  as_is: undefined,
  make_offer: undefined,
  awaiting_info_to_complete: undefined,
  exchange_only: undefined,
  after_hours_aog: undefined,
  sellable_quantity: undefined
};

const defaultFiltersbyAllOffer = {
  no_quote: undefined,
  offer: undefined,
  looking_for: undefined,
  no_interest: undefined
};
const filters = [
  {
    label: "My Outgoing Quotes",
    filters: { ...defaultFilters, current_user: true }
  },
  {
    label: "All No Quotes",
    filters: { ...defaultFilters, no_quote: true }
  },
  {
    label: "All Offers",
    filters: {
      ...defaultFiltersbyAllOffer,
      offer: true,
      looking_for: false,
      no_interest: false
    }
  },
  {
    label: "All Offers with Looking For",
    filters: { looking_for: true }
  },
  {
    label: "All Offers with No Interest",
    filters: { no_interest: true }
  },
  {
    label: "All Outgoing Quotes",
    filters: defaultFilters
  },
  {
    label: "Quote Type Standard",
    filters: { standard: true }
  },
  {
    label: "Quote Type As Is",
    filters: { as_is: true }
  },
  {
    label: "Quote Type Make Offer",
    filters: { make_offer: true }
  },
  {
    label: "Quote Type Awaiting Info to Complete",
    filters: { awaiting_info_to_complete: true }
  },
  {
    label: "Quote Type Exchange Only",
    filters: { exchange_only: true }
  },
  {
    label: "Quote Type After Hours AOG",
    filters: { after_hours_aog: true }
  },
  {
    label: "OQs with Internal Notes",
    filters: { has_internal_note: true }
  },
  {
    label: "All Followed Up On",
    filters: { followed_up: true }
  },
  {
    label: "Not Followed Up On",
    filters: { followed_up: false }
  },
  {
    label: "Sellable Quantity",
    filters: { sellable_quantity: true }
  },
  {
    label: "No Sellable Quantity",
    filters: { sellable_quantity: false }
  }
];

const filterTags = [
  {
    label: "OQs with Internal Notes",
    key: "has_internal_note"
  },
  {
    label: "Show My OQs",
    key: "current_user"
  },
  {
    label: "Show OQs - Offer",
    key: "offer"
  },
  {
    label: "Show OQs - No Quote",
    key: "no_quote"
  },
  {
    label: "Looking For",
    key: "looking_for",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "No Interest",
    key: "no_interest",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "All Followed Up On",
    key: "followed_up",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Not Followed Up On",
    key: "followed_up",
    matchesTarget: matchesBoolean(false)
  },
  {
    label: "Quote Type Standard",
    key: "standard",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Quote Type As Is",
    key: "as_is",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Quote Type Make Offer",
    key: "make_offer",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Quote Type Awaiting Info to Complete",
    key: "awaiting_info_to_complete",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Quote Type Exchange Only",
    key: "exchange_only",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Quote Type After Hours AOG",
    key: "after_hours_aog",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Sellable Quantity",
    key: "sellable_quantity",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "No Sellable Quantity",
    key: "sellable_quantity",
    matchesTarget: matchesBoolean(false)
  }
];

const ExportButton = ({ searchProps }) => {
  const params = searchProps.paginator.get("filters").toJS();
  const totalCount = searchProps.paginator.get("totalCount");

  params.total_count = totalCount;
  return (
    <Button
      onClick={() => {
        if (totalCount > EXPORT_LIMIT) {
          toastr.info("Processing Report.");
          api.outgoingQuotes.export(params).then(() => SUCCESS);
        } else {
          window.open(
            `${API_BASE}/outgoing_quotes/export?${stringify(params)}`,
            "_blank"
          );
          toastr.success(
            "Processing Outgoing Quotes Report. Please wait for the PDF to generate."
          );
        }
      }}
    >
      Export
    </Button>
  );
};

const meetsExportCriteria = renderIf(({ searchProps }) => {
  const user = useCurrentUser();
  return (
    searchProps.paginator.get("totalCount") <= EXPORT_LIMIT ||
    hasRole(user, ADMIN)
  );
});

const DecoratedExportButton = meetsExportCriteria(ExportButton);

function QuotesSearch({ pushHistory }) {
  return (
    <Search search={pagination} syncWithUrl>
      {searchProps => (
        <div>
          <div className="form-group form-group-searchbar has-feedback has-feedback-left">
            <i className="text-muted fa fa-search form-control-feedback form-control-feedback-left" />
            <div className="input-group">
              <SearchFilter
                {...searchProps}
                placeholder="Search by typing a customer name, outgoing quote number, part number, buyer, seller or date created..."
                submitOnSingleResult={r =>
                  pushHistory(`/outgoing-quotes/${r.outgoing_quote_id}`)}
              />
              <DecoratedExportButton searchProps={searchProps} />
              <DateRangePicker {...searchProps} />
              <FilterSelect {...searchProps} filters={filters} right />
            </div>
          </div>
          <TitleWithFilters
            {...searchProps}
            title="Outgoing Quotes"
            filters={filterTags}
          />
          <Table {...searchProps} />
          <Pagination {...searchProps} />
        </div>
      )}
    </Search>
  );
}

QuotesSearch.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default connect(undefined, { pushHistory: push })(QuotesSearch);



// WEBPACK FOOTER //
// ./app/modules/OutgoingQuotes/Search/OutgoingQuotes.jsx